// client/src/pages/SectorManager/Dashboard/styles.js
import styled from 'styled-components';

// ========== Layout ==========
export const DashboardContainer = styled.div`
  min-height: 100vh;
  background: #e1e1e1;
  padding: 20px;
  direction: rtl;
`;

export const Header = styled.header`
  background: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
`;

export const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`;

export const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const Avatar = styled.div`
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 700;
`;

export const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

export const Greeting = styled.span`
  font-size: 0.9rem;
  color: #666;
`;

export const WelcomeText = styled.h1`
  color: #333;
  font-size: 1.8rem;
  margin: 0;
  font-weight: 600;
`;

export const RoleBadge = styled.span`
  font-size: 0.8rem;
  color: #667eea;
  background: rgba(102, 126, 234, 0.1);
  padding: 4px 12px;
  border-radius: 20px;
  margin-top: 4px;
  display: inline-block;
`;

export const SectorTags = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

export const SectorTag = styled.span`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 0.75rem;
  font-weight: 500;
`;

export const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

// ========== Buttons ==========
export const Button = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  border: none;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  ${props => props.variant === 'primary' && `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
    }
  `}

  ${props => props.variant === 'secondary' && `
    background: #f8f9fa;
    color: #333;
    border: 1px solid #e9ecef;
    &:hover { background: #e9ecef; }
  `}

  ${props => props.variant === 'danger' && `
    background: transparent;
    color: #e74c3c;
    border: 1px solid #e74c3c;
    &:hover { background: #e74c3c; color: white; }
  `}

  ${props => props.variant === 'success' && `
    background: #27ae60;
    color: white;
    &:hover { background: #219a52; }
  `}

  ${props => props.variant === 'warning' && `
    background: #f39c12;
    color: white;
    &:hover { background: #d68910; }
  `}

  ${props => props.small && `
    padding: 6px 12px;
    font-size: 0.8rem;
  `}

  ${props => props.block && `
    width: 100%;
    justify-content: center;
  `}
`;

// ========== Stats ==========
export const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

export const StatCard = styled.div`
  background: white;
  padding: 25px;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  text-align: center;
  transition: transform 0.3s ease;
  border-right: 4px solid ${props => props.color || '#667eea'};
  
  &:hover {
    transform: translateY(-5px);
  }
`;

export const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: bold;
  color: ${props => props.color || '#667eea'};
  margin-bottom: 8px;
`;

export const StatLabel = styled.div`
  color: #666;
  font-size: 0.9rem;
  font-weight: 500;
`;

// ========== Progress Ring ==========
export const ProgressSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  gap: 40px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

export const ProgressRing = styled.div`
  position: relative;
  width: 140px;
  height: 140px;

  svg {
    transform: rotate(-90deg);
    width: 100%;
    height: 100%;
  }

  .ring-bg {
    fill: none;
    stroke: #e9ecef;
    stroke-width: 10;
  }

  .ring-fill {
    fill: none;
    stroke: url(#gradient);
    stroke-width: 10;
    stroke-linecap: round;
    transition: stroke-dasharray 0.5s ease;
  }
`;

export const RingText = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
`;

export const RingNumber = styled.span`
  display: block;
  font-size: 1.8rem;
  font-weight: 700;
  color: #667eea;
`;

export const RingLabel = styled.span`
  font-size: 0.8rem;
  color: #666;
`;

export const ProgressDetails = styled.div`
  h3 {
    margin: 0 0 8px;
    font-size: 1.2rem;
    color: #333;
  }

  p {
    margin: 0;
    color: #666;
    font-size: 0.95rem;
  }
`;

// ========== Tabs ==========
export const Tabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  background: white;
  padding: 8px;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  flex-wrap: wrap;
`;

export const Tab = styled.button`
  flex: 1;
  min-width: 120px;
  padding: 12px 24px;
  background: ${props => props.active ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent'};
  color: ${props => props.active ? 'white' : '#666'};
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.active ? '' : '#f8f9fa'};
  }
`;

// ========== Cards ==========
export const Section = styled.section`
  background: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
`;

export const SectionTitle = styled.h2`
  color: #333;
  font-size: 1.5rem;
  margin: 0 0 25px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 16px;
`;

// ========== Employee Cards ==========
export const EmployeeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

export const EmployeeCard = styled.div`
  background: #fff;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;
  border-right: 4px solid ${props => {
    if (props.status === 'completed') return '#27ae60';
    if (props.status === 'in_progress') return '#3498db';
    if (props.status === 'not_started') return '#e74c3c';
    return '#f39c12';
  }};
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
`;

export const EmployeeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

export const EmployeeAvatar = styled.div`
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  font-weight: 600;
`;

export const EmployeeInfo = styled.div`
  flex: 1;
`;

export const EmployeeName = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  color: #333;
`;

export const EmployeeEmail = styled.p`
  margin: 2px 0 0;
  font-size: 0.8rem;
  color: #666;
`;

export const StatusBadge = styled.span`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => {
    if (props.status === 'completed') return '#d1fae5';
    if (props.status === 'in_progress') return '#dbeafe';
    if (props.status === 'failed' || props.status === 'not_started') return '#fee2e2';
    return '#fef3c7';
  }};
  color: ${props => {
    if (props.status === 'completed') return '#065f46';
    if (props.status === 'in_progress') return '#1e40af';
    if (props.status === 'failed' || props.status === 'not_started') return '#991b1b';
    return '#92400e';
  }};
`;

// ========== Progress Bar ==========
export const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
`;

export const ProgressFill = styled.div`
  height: 100%;
  background: ${props => props.danger ? '#e74c3c' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  width: ${props => props.width}%;
  transition: width 0.3s ease;
`;

export const ProgressText = styled.div`
  font-size: 0.8rem;
  color: #666;
  display: flex;
  justify-content: space-between;
`;

// ========== Course Cards ==========
export const CourseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

export const CourseCard = styled.div`
  background: #fff;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;
  border-right: 4px solid ${props => {
    switch(props.status) {
      case 'completed': return '#27ae60';
      case 'in_progress': return '#3498db';
      case 'pending': return '#f39c12';
      case 'failed': return '#e74c3c';
      default: return '#95a5a6';
    }
  }};
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
`;

export const CourseTitle = styled.h3`
  color: #333;
  font-size: 1.2rem;
  margin-bottom: 10px;
  font-weight: 600;
`;

export const CourseDescription = styled.p`
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 15px;
  line-height: 1.5;
`;

export const CourseMeta = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  font-size: 0.8rem;
  color: #999;
`;

export const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const ActionButtons = styled.div`
  display: grid;
  grid-template-columns: ${props => props.columns || '1fr'};
  gap: 10px;
`;

// ========== Table ==========
export const TableContainer = styled.div`
  overflow-x: auto;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 12px 16px;
    text-align: right;
    border-bottom: 1px solid #e9ecef;
  }

  th {
    background: #f8f9fa;
    font-weight: 600;
    color: #666;
    font-size: 0.85rem;
  }

  tr:hover td {
    background: #f8f9fa;
  }
`;

// ========== Filter Bar ==========
export const FilterBar = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  align-items: center;
`;

export const Select = styled.select`
  padding: 10px 16px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: white;
  color: #333;
  font-size: 0.95rem;
  min-width: 180px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

export const SearchInput = styled.input`
  padding: 10px 16px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: white;
  color: #333;
  font-size: 0.95rem;
  min-width: 200px;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

// ========== Status Cards ==========
export const StatusCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

export const StatusCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border-top: 4px solid ${props => props.color || '#667eea'};
`;

export const StatusCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

export const StatusCardTitle = styled.h4`
  margin: 0;
  font-size: 1rem;
  color: #666;
`;

export const StatusCardNumber = styled.span`
  font-size: 2.5rem;
  font-weight: 700;
  color: #333;
`;

export const StatusCardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
`;

export const StatusCardItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 8px;
  font-size: 0.85rem;
`;

export const StatusCardMore = styled.span`
  font-size: 0.8rem;
  color: #667eea;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`;

// ========== Modal ==========
export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

export const Modal = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  max-width: 700px;
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;
  position: relative;
`;

export const ModalClose = styled.button`
  position: absolute;
  top: 16px;
  left: 16px;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #666;
  cursor: pointer;
  
  &:hover {
    color: #333;
  }
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`;

export const ModalAvatar = styled.div`
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 600;
`;

export const ModalTitle = styled.h2`
  margin: 0;
  color: #333;
`;

export const ModalSubtitle = styled.p`
  margin: 4px 0 0;
  color: #666;
  font-size: 0.9rem;
`;

export const ModalStats = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 24px;

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const ModalStat = styled.div`
  text-align: center;
  background: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
`;

export const ModalStatValue = styled.span`
  display: block;
  font-size: 1.5rem;
  font-weight: 700;
  color: #667eea;
`;

export const ModalStatLabel = styled.span`
  font-size: 0.75rem;
  color: #666;
`;

export const ModalSection = styled.div`
  margin-bottom: 24px;
`;

export const ModalSectionTitle = styled.h3`
  font-size: 1rem;
  color: #333;
  margin: 0 0 12px;
`;

export const ModalCourseList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const ModalCourse = styled.div`
  background: #f8f9fa;
  padding: 12px;
  border-radius: 8px;
`;

export const ModalCourseHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

export const ModalCourseTitle = styled.span`
  font-weight: 500;
  color: #333;
`;

export const ModalCourseProgress = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.85rem;
  color: #666;
`;

export const Score = styled.span`
  color: #27ae60;
  font-weight: 600;
`;

// ========== Empty State ==========
export const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
`;

export const EmptyIcon = styled.span`
  font-size: 4rem;
  display: block;
  margin-bottom: 16px;
`;

export const EmptyTitle = styled.h3`
  margin: 0 0 8px;
  color: #333;
`;

export const EmptyText = styled.p`
  margin: 0;
  color: #666;
`;

// ========== Loading ==========
export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  gap: 16px;
`;

export const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid #e9ecef;
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

export const LoadingText = styled.p`
  color: #666;
  font-size: 1rem;
`;

// ========== Alerts ==========
export const Alert = styled.div`
  padding: 16px 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;

  ${props => props.type === 'success' && `
    background: #d1fae5;
    color: #065f46;
    border: 1px solid #a7f3d0;
  `}

  ${props => props.type === 'error' && `
    background: #fee2e2;
    color: #991b1b;
    border: 1px solid #fecaca;
  `}

  ${props => props.type === 'warning' && `
    background: #fef3c7;
    color: #92400e;
    border: 1px solid #fde68a;
  `}

  ${props => props.type === 'info' && `
    background: #dbeafe;
    color: #1e40af;
    border: 1px solid #bfdbfe;
  `}
`;

export const AlertClose = styled.button`
  margin-right: auto;
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  opacity: 0.7;
  
  &:hover {
    opacity: 1;
  }
`;

// ========== Checkbox ==========
export const Checkbox = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  color: #333;

  input {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
`;

// ========== Action Menu ==========
export const ActionMenu = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;